import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

//Components
import { Button } from "@gpstrackit/react-controls"

//Views
import DataView from 'Components/DataView';
import ChipStatus from 'Components/ChipStatus';
import CreateEditMessageTemplate from '../createEditMessageTemplate';
import DeleteMessageTemplate from '../deleteMessageTemplate';

//Security
import { HasPermission } from "Core/security";

//Notification
import { NotificationManager } from "Components/Notifications";

//Helpers
import { getMessageTemplateCodes } from 'Core/data/Helpers';

//Actions
import {
    messageTemplateDeleteOpenModal,
    messageTemplateCreateEditOpenModal,
    clearRedux
} from 'Redux/actions';
import { get } from "lodash";

const MessagesTemplates = (props) => {

    const { height, entityName, entityId } = props;

    const { messages } = useIntl();
    const tableRef = useRef();
    const dispatch = useDispatch();


    const user = useSelector(state => state.securityRedux.user);
    const openModalCreateEdit = useSelector(state => state.createEditMessageTemplateRedux.openModal);
    const openModalDelete = useSelector(state => state.deleteMessageTemplateRedux.openModal);

    const [columns, setColumns] = useState([
        {
            key: "template",
            field: "templateCode",
            title: messages['messages_templates_template'],
            width: 150,
            show: true,
            locked: false,
            resize: true,
            code: "messages_templates_template",
            render: (item) => {
                const code = getMessageTemplateCodes(messages).find((code) => code?.id === item?.templateCode);
                return (
                    <span>{code?.label || item?.templateCode}</span>
                )
            },
            export: true,
            functionString: 'return row?.templateCode'
        },
        {
            key: "language",
            field: "language",
            title: messages['messages_templates_language'],
            width: 100,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            code: "messages_templates_language",
            render: (item) => {
                return <span>{item?.language}</span>
            },
            export: true,
            functionString: 'return row?.language'
        },
        {
            key: "emailFrom",
            field: "emailFrom",
            title: messages['messages_templates_email_from'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            code: "messages_templates_email_from",
            render: (item) => {
                return <span>{item?.emailFrom}</span>
            },
            export: true,
            functionString: 'return row?.emailFrom'
        },
        {
            key: "emailSubject",
            field: "emailSubject",
            title: messages['messages_templates_email_subject'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            code: "messages_templates_email_subject",
            render: (item) => {
                return <span>{item?.emailSubject}</span>
            },
            export: true,
            functionString: 'return row?.emailSubject'
        }

    ])
    
    const [searchFields, setSearchFields] = useState(["templateCode", "emailFrom", "language"]);
    
   useEffect(() => {
        return () => {
            dispatch(clearRedux("COMMON", {
                'messages-templates-tab': {}
            }))
        }
    }, [])

    return (
        <div style={{ marginTop: 10 }}>
            <DataView
                ref={tableRef}
                exportName="messages-templates-tab"
                dinamicQuery
                name="pm-messages-templates-tab"
                entity="messages-templates-tab"
                graphqlFunction="searchMessageTemplates"
                graphqlSort="MessageTemplateSortInput"
                graphqlConditions="MessageTemplateSearchInput"
                customQuery="id "
                columns={columns}
                hideAllInPagination
                noFilters
                height={height || 500}
                //search
                asyncSelect
                searchConditions={["templateCode", "emailFrom", "language"]}
                searchOptionValue="id"
                searchOptionLabel="templateCode"
                customSearch={{ fields: ["entityNameId"], terms: [`${entityName}Id_${entityId}`], exact: true, conditionOperator: "AND" }}
                onChangeConditions={(tags, conditions, total, newColumns, rows, sort) => {
                    //current conditions
                    let currentConditions = {}
                    let currentTags = {}

                    if (!conditions && tags) {
                        currentConditions = tableRef?.current?.conditions;
                        currentTags = tags;
                    } else {
                        currentConditions = conditions || [];
                        currentTags = currentConditions?.filter((x) => x?.tags)[0]?.tags || [];
                        if (currentTags && currentTags.length > 0) tableRef?.current?.setTags(currentTags);
                    }

                    currentConditions = currentConditions?.filter((item) => item?.fields[0] !== 'id' && item?.fields[0] !== 'templateCode' && item?.fields[0] !== 'entityNameId');

                    let tagsById = currentTags.filter((item) => typeof item.id === "number");
                    if (tagsById?.length > 0) {
                        //New Items
                        let currentIds = tagsById.map((item) => (item.id));
                        if (currentIds.length > 0) {
                            currentConditions.push({ fields: ["id"], terms: currentIds, exact: true, tags: currentTags, conditionOperator: "OR" });
                        }
                    }

                    let tagsByLabel = currentTags.filter((item) => typeof item.id === "string");

                    if (tagsByLabel.length > 0) {
                        let currentLabels = tagsByLabel.map((item) => (item.name))
                        if (currentLabels.length > 0) {
                            currentConditions.push({ fields: searchFields, terms: currentLabels, exact: false, tags: currentTags, conditionOperator: "OR" })
                        }
                    }

                    currentConditions.push({ fields: ["entityNameId"], terms: [`${entityName}Id_${entityId}`], exact: true, conditionOperator: "AND" });

                    if (!conditions && tags) tableRef?.current?.getData(null, null, null, currentConditions);
                    else tableRef?.current?.getData(null, rows, sort, currentConditions, newColumns);
                }}
                topheight={200}
                noSplit
                onChangeColumns={(items) => {
                    let currentColumns = items.map((item) => ({
                        ...item,
                        title: item.code ? messages[item.code] : item.title,
                        render: item.key === "status" ? (item) => {
                            return (
                                <div style={{ display: 'flex' }}>
                                    <ChipStatus statusCode={item?.status} messages={messages} />
                                </div>
                            )
                        } : item.render,
                    }))
                    setColumns(currentColumns)
                }}
                tableButtons={["export", 'orderColumn']}
                options={[
                    {
                        id: "messages_templates_edit",
                        label: messages['messages_templates_edit'],
                        single: true,
                        show: true,
                        disabled: !HasPermission(user, "mssgtmpt", "u"),
                        action: (items) => {
                            let currentItem = items.find(item => item.id);
                            dispatch(messageTemplateCreateEditOpenModal(true, currentItem))
                        }
                    },
                    {
                        id: "messages_templates_delete",
                        label: messages['messages_templates_delete'],
                        single: true,
                        show: true,
                        disabled: !HasPermission(user, "mssgtmpt", "d"),
                        action: (items) => {
                            let currentItem = items.find(item => item.id);
                            dispatch(messageTemplateDeleteOpenModal(true, currentItem))
                        }
                    }
                ]}
                menuOptions={() => {
                    return [
                        messages['messages_templates_edit'],
                        messages['messages_templates_delete']
                    ]
                }}
                onClickMenu={(event, item) => {
                    if (event === messages['messages_templates_edit']) {
                        if (HasPermission(user, "mssgtmpt", "u")) {
                            dispatch(messageTemplateCreateEditOpenModal(true, item))
                        } else {
                            NotificationManager.warning(
                                messages['common_error_permission'],
                                messages['messages_templates_tab_templates_title'],
                                5000,
                            );
                        }
                    }
                    if (event === messages['messages_templates_delete']) {
                        if (HasPermission(user, "mssgtmpt", "d")) {
                            dispatch(messageTemplateDeleteOpenModal(true, item))
                        } else {
                            NotificationManager.warning(
                                messages['common_error_permission'],
                                messages['messages_templates_tab_templates_title'],
                                5000,
                            );
                        }
                    }
                }}
                extraButton={() => {
                    return (
                        <div style={{ margin: '0 5px' }}>
                            <Button
                                disabled={!HasPermission(user, "mssgtmpt", "c")}
                                className="btn-dark-blue"
                                onClick={(e) => {
                                    e.preventDefault();
                                    dispatch(messageTemplateCreateEditOpenModal(true))
                                }}
                            >
                                {messages['messages_templates_add_message_template']}
                            </Button>
                        </div>
                    )
                }}
            />
            {openModalCreateEdit && <CreateEditMessageTemplate tableRef={tableRef} entityName={entityName} entityId={entityId} />}
            {openModalDelete && <DeleteMessageTemplate tableRef={tableRef} />}
        </div>
    )
}
export default MessagesTemplates;